import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getStatusColor } from '../../lib/statusColors';

import { 
  ChevronDown, 
  ChevronRight, 
  Download, 
  Eye, 
  Calendar, 
  FileText, 
  Building,
  Upload,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileUp,
  Package,
  X,
  Trash2,
  AlertTriangle,
  Send
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { FolderTreeView } from "@/components/packages/FolderTreeView";

interface TenderPackage {
  id: string;
  name: string;
  packageNumber: string;
  description?: string;
  totalFiles: number;
  createdAt: string;
  submissionDeadline?: string;
  sentAt: string;
  status: string;
  hubName?: string;
  projectName?: string;
  shareToken?: string;
}

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

interface FileItem {
  id: string;
  name: string;
  size?: number;
  version?: string;
  createdBy?: string;
  itemType?: 'file' | 'folder';
  type?: 'file' | 'folder';
  path?: string;
}

interface ExpandablePackageRowProps {
  pkg: TenderPackage;
  authData: TendererAuth;
  onDownload: (pkg: TenderPackage) => void;
  isDownloading: boolean;
}

export function ExpandablePackageRow({ pkg, authData, onDownload, isDownloading }: ExpandablePackageRowProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submissionNote, setSubmissionNote] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Fetch package details when expanded
  const { data: packageDetails, isLoading: detailsLoading } = useQuery<{files: FileItem[], totalFiles: number}>({
    queryKey: [`/api/tenderer/package-details/${pkg.id}`, authData?.email, authData?.accessCode],
    queryFn: () => apiRequest(`/api/tenderer/package-details/${pkg.id}`, {
      method: 'POST',
      body: JSON.stringify({
        email: authData?.email,
        accessCode: authData?.accessCode
      })
    }),
    enabled: expanded && !!authData?.email && !!authData?.accessCode,
  });

  // Check if already submitted - only check when expanded to reduce unnecessary queries
  const { data: submittedReturn } = useQuery<any>({
    queryKey: [`/api/tenderer/submitted-return/${pkg.id}`, authData?.email, authData?.accessCode],
    queryFn: () => apiRequest(`/api/tenderer/submitted-return/${pkg.id}`, {
      method: 'POST',
      body: JSON.stringify({
        email: authData?.email,
        accessCode: authData?.accessCode
      })
    }),
    enabled: expanded && !!authData?.email && !!authData?.accessCode, // Only query when expanded
    refetchInterval: false, // Disable automatic refetching to prevent status changes
  });

  const submitTenderMutation = useMutation({
    mutationFn: async (formData: FormData) => {
                  const response = await fetch('/api/tenderer/submit-return', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit tender');
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Tender Submitted",
        description: "Your tender response has been submitted successfully.",
      });
      setShowSubmitForm(false);
      setSubmissionNote("");
      setUploadedFiles([]);
      // Invalidate both the submitted return query and the packages list
      queryClient.invalidateQueries({ queryKey: [`/api/tenderer/submitted-return/${pkg.id}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/tenderer/packages'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tenderer/submitted-returns'] });
    },
    onError: (error) => {
      toast({
        title: "Submission Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmitTender = () => {
    if (!submissionNote.trim() && uploadedFiles.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please add a note or upload files before submitting.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append('recipientEmail', authData.email);
    formData.append('companyAccessCode', authData.accessCode);
    formData.append('packageId', pkg.id);
    formData.append('notes', submissionNote);
    formData.append('recipientName', authData.email?.split('@')[0] || 'Unknown');
    formData.append('recipientCompany', authData.companyName || 'Unknown');

    uploadedFiles.forEach((file) => {
      formData.append('files', file);
    });

    submitTenderMutation.mutate(formData);
  };

  const handleFileSelect = (fileId: string) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSelectAll = () => {
    if (!packageDetails?.files) return;
    
    if (selectedFiles.length === packageDetails.files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(packageDetails.files.map(f => f.id));
    }
  };

  const handleViewPackage = async () => {
    try {
      // Track package view only on eye icon click
                  await fetch('/api/tenderer/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authData?.email,
          accessCode: authData?.accessCode,
          packageId: pkg.id,
          viewType: 'general'
        })
      });
      
      toast({
        title: "Package Viewed",
        description: "Package viewing activity recorded",
      });
    } catch (error) {
      console.error('Error tracking package view:', error);
    }
  };

  const getDeadlineStatus = (deadline?: string) => {
    if (!deadline) return null;
    
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const daysUntil = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      return { status: 'overdue', color: 'text-red-600', bgColor: 'bg-red-50 border-red-200', icon: AlertCircle };
    } else if (daysUntil <= 7) {
      return { status: 'urgent', color: 'text-orange-600', bgColor: 'bg-orange-50 border-orange-200', icon: Clock };
    } else {
      return { status: 'normal', color: 'text-green-600', bgColor: 'bg-green-50 border-green-200', icon: CheckCircle2 };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const deadlineStatus = getDeadlineStatus(pkg.submissionDeadline);

  // Function to get row background color based on status
  const getRowBackgroundColor = (status: string) => {
    const statusColors = {
      'sent': 'bg-red-50',
      'received': 'bg-red-50',
      'viewed': 'bg-purple-50',
      'downloaded': 'bg-orange-50',
      'tender_returned': 'bg-blue-50',
      'awarded': 'bg-green-50',
      'won': 'bg-green-50',
    };
    return statusColors[status.toLowerCase() as keyof typeof statusColors] || 'bg-white';
  };

  return (
    <div className="bg-white">
      {/* Main Row - Grid Layout matching headers */}
      <div 
        className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${getRowBackgroundColor(pkg.status)}`}
        onClick={async () => {
          setExpanded(!expanded);
          // Track viewing activity on row click
          try {
            const response = await fetch('/api/tenderer/track-view', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: authData?.email,
                accessCode: authData?.accessCode,
                packageId: pkg.id
              })
            });
            
            if (response.ok) {
              // Only invalidate queries after successful API call
              await queryClient.invalidateQueries({ queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode] });
              // Force refetch to get updated status
              await queryClient.refetchQueries({ queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode] });
            }
          } catch (error) {
            console.error('Error tracking package view:', error);
          }
        }}
      >
        <div className="grid grid-cols-12 gap-1 md:gap-2 lg:gap-3 items-center text-sm text-gray-900 w-full">
          {/* Expand Button - 1 column */}
          <div className="col-span-1 flex items-center justify-start">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md"
              onClick={async (e) => {
                e.stopPropagation();
                setExpanded(!expanded);
                // Track viewing activity on expanding icon click
                try {
                  const response = await fetch('/api/tenderer/track-view', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      email: authData?.email,
                      accessCode: authData?.accessCode,
                      packageId: pkg.id
                    })
                  });
                  
                  if (response.ok) {
                    // Only invalidate queries after successful API call
                    await queryClient.invalidateQueries({ queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode] });
                    // Force refetch to get updated status
                    await queryClient.refetchQueries({ queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode] });
                  }
                } catch (error) {
                  console.error('Error tracking package view:', error);
                }
              }}
            >
              {expanded ? <ChevronDown className="h-5 w-5 text-blue-600" /> : <ChevronRight className="h-5 w-5 text-blue-600" />}
            </Button>
          </div>
          
          {/* Package Name - Reduced from 4/3 to 3/2 */}
          <div className="col-span-3 md:col-span-2 min-w-0">
            <div 
              className="flex flex-col cursor-pointer hover:text-blue-600 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              <div className="flex items-start gap-2">
                <span className="font-medium text-blue-600 hover:text-blue-800 text-sm leading-tight break-words">{pkg.packageNumber} - {pkg.name}</span>
              </div>
              <div className="flex flex-col gap-1">
                <Badge className={`${getStatusColor(pkg.status)} inline-block w-fit`} variant="secondary">
                  {pkg.status === 'tender_returned' ? 'Returned' :
                   pkg.status === 'downloaded' ? 'Downloaded' :
                   pkg.status === 'viewed' ? 'Viewed' : 
                   pkg.status === 'sent' ? 'Received' :
                   pkg.status === 'awarded' ? 'Awarded' :
                   pkg.status === 'rejected' ? 'Rejected' :
                   pkg.status === 'won' ? 'Won' :
                   pkg.status === 'lost' ? 'Lost' :
                   pkg.status.replace('_', ' ')}
                </Badge>
                {submittedReturn && submittedReturn.submittedAt && (
                  <span className="text-xs text-gray-600">
                    Returned: {new Date(submittedReturn.submittedAt).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Hub Name - Consistent with header: col-span-2 */}
          <div className="hidden md:block col-span-2 min-w-0">
            <span className="truncate block text-xs text-gray-600">{pkg.hubName || '-'}</span>
          </div>
          
          {/* Project Name - Reduced from 3/3/2 to 3/2 */}
          <div className="col-span-3 md:col-span-2 min-w-0">
            <span className="text-xs text-gray-600 leading-tight break-words">{pkg.projectName || '-'}</span>
          </div>
          
          {/* Files - 1 column */}
          <div className="col-span-1 text-center">
            <span className="text-xs font-medium bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{pkg.totalFiles || 3}</span>
          </div>
          
          {/* Received at - Hidden on mobile, 1 column on tablet+ */}
          <div className="hidden md:block col-span-1 text-center">
            <div className="group relative cursor-help">
              <span className="text-xs text-gray-700 font-medium">
                {pkg.sentAt ? formatDate(pkg.sentAt) : 'Jul 21, 2025'}
              </span>
              {/* Tooltip with exact time */}
              <div className="absolute invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap z-50">
                {pkg.sentAt ? new Date(pkg.sentAt).toLocaleString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric',
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                }) : 'Jul 21, 2025 at 2:30 PM'}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
          
          {/* Deadline - Hidden on mobile, 1 column on tablet+ */}
          <div className="hidden md:block col-span-1 text-center">
            <span className="text-xs text-orange-600 font-medium">{pkg.submissionDeadline ? formatDate(pkg.submissionDeadline) : 'Jul 25, 2025'}</span>
          </div>
          
          {/* Actions - 2 columns always to ensure space */}
          <div className="col-span-2 flex items-center justify-center gap-1 px-1">
            <div className="group relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={async (e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                  // Track viewing activity on viewing button click
                  try {
                    const response = await fetch('/api/tenderer/track-view', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        email: authData?.email,
                        accessCode: authData?.accessCode,
                        packageId: pkg.id
                      })
                    });
                    
                    if (response.ok) {
                      // Only invalidate queries after successful API call
                      await queryClient.invalidateQueries({ queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode] });
                      // Force refetch to get updated status
                      await queryClient.refetchQueries({ queryKey: ['/api/tenderer/packages', authData?.email, authData?.accessCode] });
                    }
                  } catch (error) {
                    console.error('Error tracking package view:', error);
                  }
                }}
                className="h-8 w-8 p-0 hover:bg-blue-50 text-blue-600"
              >
                <Eye className="h-4 w-4" />
              </Button>
              {/* Tooltip */}
              <div className="absolute invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap z-50">
                View
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
            
            <div className="group relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(pkg);
                }}
                disabled={isDownloading}
                className="h-8 w-8 p-0 hover:bg-green-50 text-green-600"
              >
                {isDownloading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
              {/* Tooltip */}
              <div className="absolute invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap z-50">
                Download
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
            
            <div className="group relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (submittedReturn?.status !== 'submitted' && pkg.status !== 'awarded' && pkg.status !== 'tender_returned' && pkg.status !== 'rejected') {
                    setShowSubmitForm(true);
                  }
                }}
                disabled={submittedReturn?.status === 'submitted' || pkg.status === 'awarded' || pkg.status === 'tender_returned' || pkg.status === 'rejected'}
                className={`h-8 w-8 p-0 ${
                  submittedReturn?.status === 'submitted' || pkg.status === 'awarded' || pkg.status === 'tender_returned' || pkg.status === 'rejected'
                    ? 'bg-gray-100 text-gray-400 hover:bg-gray-100 cursor-not-allowed border border-gray-300' 
                    : 'text-orange-600 hover:bg-orange-50 border border-orange-200'
                }`}
              >
                {submittedReturn ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : pkg.status === 'awarded' ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : pkg.status === 'tender_returned' ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : pkg.status === 'rejected' ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
              {/* Tooltip */}
              <div className="absolute invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap z-50">
                {submittedReturn 
                  ? 'Tender Return Submitted' 
                  : pkg.status === 'awarded' 
                    ? 'Package Already Awarded' 
                    : pkg.status === 'tender_returned'
                      ? 'Tender Already Returned'
                      : pkg.status === 'rejected'
                        ? 'Package Rejected'
                        : 'Submit Tender'
                }
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-6 space-y-6">
            {/* Package Summary - Responsive Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              <Card className="border-gray-200 bg-transparent">
                <CardContent className="p-3 text-center flex flex-col justify-center h-16">
                  <div className="text-2xl font-bold text-blue-600 mb-1">{packageDetails?.totalFiles || pkg.totalFiles || 2}</div>
                  <div className="text-xs font-medium text-blue-800 uppercase tracking-wider">TOTAL FILES</div>
                </CardContent>
              </Card>
              
              <Card className="border-gray-200 bg-transparent">
                <CardContent className="p-3 text-center flex flex-col justify-center h-16">
                  <div className="text-sm font-bold text-blue-600 mb-1">
                    <Badge className={`${getStatusColor(pkg.status)} inline-block w-fit`} variant="secondary">
                      {pkg.status === 'tender_returned' ? 'Returned' : 
                       pkg.status === 'awarded' ? 'Awarded' :
                       pkg.status === 'rejected' ? 'Rejected' :
                       pkg.status === 'won' ? 'Won' :
                       pkg.status === 'lost' ? 'Lost' :
                       pkg.status === 'downloaded' ? 'Downloaded' :
                       pkg.status === 'viewed' ? 'Viewed' : 
                       pkg.status === 'sent' ? 'Received' : 'Received'}
                    </Badge>
                  </div>
                  <div className="text-xs font-medium text-blue-800 uppercase tracking-wider">STATUS</div>
                </CardContent>
              </Card>
              
              <Card className="border-gray-200 bg-transparent">
                <CardContent className="p-3 text-center flex flex-col justify-center h-16">
                  <div className="text-sm font-bold text-gray-900 mb-1">{formatDate(pkg.sentAt)}</div>
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wider">SENT DATE</div>
                </CardContent>
              </Card>
              
              <Card className="border-gray-200 bg-transparent">
                <CardContent className="p-3 text-center flex flex-col justify-center h-16">
                  <div className={`text-sm font-bold mb-1 ${deadlineStatus?.color || 'text-orange-600'}`}>
                    {pkg.submissionDeadline ? formatDate(pkg.submissionDeadline) : 'Jul 25, 2025'}
                  </div>
                  <div className="text-xs font-medium text-orange-600 uppercase tracking-wider">DEADLINE</div>
                </CardContent>
              </Card>
              
              {/* Enhanced Submit Tender Response Button */}
              <div className="relative">
                <Button 
                  className={`${submittedReturn?.status === 'submitted' || pkg.status === 'awarded' || pkg.status === 'tender_returned' || pkg.status === 'rejected'
                    ? 'bg-gray-100 border-2 border-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-br from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 border-2 border-green-600 hover:border-green-700 text-white transform hover:scale-105 shadow-lg hover:shadow-xl'
                  } h-16 p-3 flex flex-col justify-center transition-all duration-300`}
                  onClick={() => {
                    if (pkg.status !== 'awarded' && pkg.status !== 'tender_returned' && pkg.status !== 'rejected') {
                      setShowSubmitForm(true);
                    }
                  }}
                  disabled={submittedReturn?.status === 'submitted' || pkg.status === 'awarded' || pkg.status === 'tender_returned' || pkg.status === 'rejected'}
                >
                  <div className="text-lg font-bold mb-1"              >
                {submittedReturn?.status === 'submitted' ? (
                  <CheckCircle2 className="h-6 w-6 mx-auto" />
                ) : pkg.status === 'awarded' ? (
                  <CheckCircle2 className="h-6 w-6 mx-auto" />
                ) : pkg.status === 'tender_returned' ? (
                  <CheckCircle2 className="h-6 w-6 mx-auto" />
                ) : pkg.status === 'rejected' ? (
                  <CheckCircle2 className="h-6 w-6 mx-auto" />
                ) : (
                  <Send className="h-6 w-6 mx-auto" />
                )}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider">
                    {submittedReturn?.status === 'submitted' ? 'SUBMITTED' : 
                     pkg.status === 'awarded' ? 'AWARDED' :
                     pkg.status === 'tender_returned' ? 'RETURNED' :
                     pkg.status === 'rejected' ? 'REJECTED' :
                     'SUBMIT TENDER'}
                  </div>
                </Button>
              </div>
            </div>

            {/* Package Contents - Professional Design */}
            <Card>
              <CardHeader className="pb-0">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg">Tender Package ({packageDetails?.totalFiles || pkg.totalFiles || 2} files)</CardTitle>
                </div>
              </CardHeader>
              
              <CardContent className="p-0">
                {detailsLoading ? (
                  <div className="space-y-2 p-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center space-x-3">
                        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="flex-1 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    {/* File Listings - Real Package Data */}
                    <div className="divide-y divide-gray-100">
                      {packageDetails?.files?.length > 0 ? (
                        <FolderTreeView 
                          items={packageDetails.files.map(file => ({
                            id: file.id,
                            name: file.name,
                            type: (file.itemType || file.type || 'file') as 'file' | 'folder',
                            path: file.path,
                            size: file.size || 0,
                            lastUpdated: new Date().toISOString(),
                            updatedBy: file.createdBy || 'System User',
                            reviewStatus: 'Current',
                            originator: file.createdBy || 'System User',
                            revision: file.version || 'V1',
                            accUrl: '',
                            versionId: file.version,
                            isOutdated: false
                          }))}
                          hideStatusColumn={true}
                          selectedItems={selectedFiles}
                          onSelectItem={(itemId, checked) => {
                            if (checked) {
                              setSelectedFiles(prev => [...prev, itemId]);
                            } else {
                              setSelectedFiles(prev => prev.filter(id => id !== itemId));
                            }
                          }}
                          onSelectAll={(checked) => {
                            if (checked) {
                              setSelectedFiles(packageDetails.files.map(file => file.id));
                            } else {
                              setSelectedFiles([]);
                            }
                          }}
                        />
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500">
                          <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p>No files in this package</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>


          </div>
        </div>
      )}
      
      {/* Enhanced Submit Tender Response Dialog */}
      {showSubmitForm && submittedReturn?.status !== 'submitted' && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={(e) => {
          if (e.target === e.currentTarget) setShowSubmitForm(false);
        }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <h2 className="text-2xl font-bold">Submit Tender Response</h2>
                  <p className="text-green-100 mt-1">Package {pkg.packageNumber} - {pkg.name}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSubmitForm(false)}
                  className="text-white hover:text-green-100 hover:bg-green-700/50 h-10 w-10 p-0"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="p-6 space-y-4">
                
                {/* Notes Section - Compact */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                      <FileText className="h-3 w-3 text-gray-600" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900">Notes</h3>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <Textarea
                      value={submissionNote}
                      onChange={(e) => setSubmissionNote(e.target.value)}
                      placeholder="Add any notes or comments about your tender response..."
                      className="min-h-[80px] bg-white border-gray-300 focus:border-green-500 focus:ring-green-500 text-sm"
                      rows={3}
                    />
                  </div>
                </div>
                
                {/* File Upload Section - Compact */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                      <Upload className="h-3 w-3 text-gray-600" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900">Supporting Documents</h3>
                  </div>
                  
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
                      <Input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
                          const validFiles: File[] = [];
                          const invalidFiles: string[] = [];
                          
                          files.forEach((file) => {
                            if (file.size > MAX_FILE_SIZE) {
                              invalidFiles.push(file.name);
                            } else {
                              validFiles.push(file);
                            }
                          });
                          
                          // Show error if any files are invalid
                          if (invalidFiles.length > 0) {
                            toast({
                              title: "File size limit exceeded",
                              description: `The following files exceed 10MB limit: ${invalidFiles.join(', ')}`,
                              variant: "destructive",
                              duration: 5000,
                            });
                          }
                          
                          // Only add valid files, merge with existing files
                          if (validFiles.length > 0) {
                            setUploadedFiles(prev => [...prev, ...validFiles]);
                          }
                          
                          // Always clear the input after processing
                          if (e.target) {
                            e.target.value = '';
                          }
                        }}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-900 mb-2">Upload Response Documents</p>
                        <div className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                          Choose Files
                        </div>
                      </label>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Accepted formats: PDF, Word, Excel, Images • Maximum 10MB per file
                    </p>
                    
                    {uploadedFiles.length > 0 && (
                      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-3">Selected Files ({uploadedFiles.length}):</p>
                        <div className="space-y-3">
                          {uploadedFiles.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newFiles = uploadedFiles.filter((_, i) => i !== index);
                                  setUploadedFiles(newFiles);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="bg-gray-50 border-t border-gray-200 px-8 py-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-gray-600">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Ensure all required documents are included before submission
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSubmitForm(false)}
                    className="px-6 py-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitTender}
                    disabled={submitTenderMutation.isPending}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-2 font-semibold"
                  >
                    {submitTenderMutation.isPending ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting Response...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Upload className="h-4 w-4" />
                        <span>Submit Tender Response</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}